import React from "react";
import Dropzone, { type DropzoneRef } from "react-dropzone";

import { faImage } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import Cropper from "cropperjs";
import "cropperjs/dist/cropper.css";

import ImageCropper from "~/common/ImageCropper";
import { __globalModalContext } from "~/ui/modals";
import toast from "~/ui/toast";

import styles from "./css/ImagePicker.module.css";

interface ImagePickerProps {
	src: string | undefined;
	circular: boolean;
	previewWidth: number;
	previewHeight: number;
	desiredWidth: number;
	desiredHeight: number;
	placeholder?: string;
	onChange?: (imageDataUrl: string | undefined) => void;
}

interface ImagePickerState {
	urlDidError: string | undefined;
	imageNaturalWidth: number;
	imageNaturalHeight: number;
}

interface Dimensions {
	width: number;
	height: number;
}

export default class ImagePicker extends React.Component<ImagePickerProps, ImagePickerState> {
	static defaultProps = {
		circular: false,
		previewWidth: 300,
		previewHeight: 150,
		desiredWidth: null,
		desiredHeight: null,
	};

	constructor(props: ImagePickerProps) {
		super(props);

		this.state = {
			urlDidError: undefined,
			imageNaturalWidth: 0,
			imageNaturalHeight: 0,
		};
	}

	dropzone = React.createRef<DropzoneRef>();
	cropper: Cropper | null = null;
	desiredWidth = 0;
	desiredHeight = 0;
	aspectRatio = 1;

	componentDidMount() {
		this.computeDimensions();
	}

	componentDidUpdate() {
		this.computeDimensions();
	}

	fitRectIntoBounds(rect: Dimensions, bounds: Dimensions): Dimensions {
		const rectRatio = rect.width / rect.height;
		const boundsRatio = bounds.width / bounds.height;

		const newDimensions = { width: 0, height: 0 };

		// Rect is more landscape than bounds - fit to width
		if (rectRatio > boundsRatio) {
			newDimensions.width = bounds.width;
			newDimensions.height = rect.height * (bounds.width / rect.width);
		}
		// Rect is more portrait than bounds - fit to height
		else {
			newDimensions.width = rect.width * (bounds.height / rect.height);
			newDimensions.height = bounds.height;
		}

		return newDimensions;
	}

	resolveImage = (url: string): Promise<string> => {
		return new Promise<string>((resolve, _reject) => {
			const image = new window.Image();
			image.crossOrigin = "anonymous";

			let loaded = false;
			image.onload = () => {
				loaded = true;
				const canvas = document.createElement("canvas");
				canvas.width = 1000;
				canvas.height = 1000;

				const imageRatio = { width: image.naturalWidth, height: image.naturalHeight };
				const canvasRatio = { width: canvas.width, height: canvas.height };
				const ratio = this.fitRectIntoBounds(imageRatio, canvasRatio);

				const ctx = canvas.getContext("2d");
				if (ctx)
					ctx.drawImage(
						image,
						(canvasRatio.width - ratio.width) / 2,
						(canvasRatio.height - ratio.height) / 2,
						ratio.width,
						ratio.height,
					);

				resolve(canvas.toDataURL("image/png"));

				this.setState({
					imageNaturalWidth: image.naturalWidth,
					imageNaturalHeight: image.naturalHeight,
				});
			};

			image.src = url;

			// Make sure the load event fires for cached images too
			if (image.complete && !loaded) {
				image.src = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==";
				image.src = url;
			}
		});
	};

	openModal = (imageDataUrl: string): void => {
		__globalModalContext?.open(({ close }) => (
			<ImageCropper
				close={close}
				aspectRatio={this.aspectRatio}
				handleCanvasReady={this.handleCanvasReady}
				circular={this.props.circular}
				handleCropperRef={this.handleCropperRef}
				src={imageDataUrl}
				handleCropperCancel={this.handleCropperCancel}
				handleCropperDone={this.handleCropperDone}
			/>
		));
	};

	handleImageSelected = (file: Blob): void => {
		const reader = new FileReader();

		reader.onload = () => this.resolveImage(reader.result as string).then(this.openModal);
		reader.readAsDataURL(file);
	};

	reject = (): void => {
		toast.warn("You can only select image files up to a maximum of 4MB.");
	};

	accept = (files: Blob[]): void => {
		this.handleImageSelected(files[0]);
	};

	onUploadClicked = (e: React.MouseEvent<HTMLSpanElement>): void => {
		e.preventDefault();
		e.stopPropagation();
		if (this.dropzone.current) this.dropzone.current.open();
	};

	handleCropperDone = (close: () => void): void => {
		this.handleImageCropped();
		close();
	};

	handleCropperCancel = (close: () => void): void => {
		if (this.cropper) this.cropper.clear();
		close();
	};

	handleImageCropped = (): void => {
		if (!this.cropper) return;

		const canvas = this.cropper.getCroppedCanvas({
			width: this.props.desiredWidth || this.props.previewWidth,
			height: this.props.desiredWidth || this.props.previewHeight,
			maxWidth: Math.min(4096, this.state.imageNaturalWidth * 2 || 4096),
			maxHeight: Math.min(4096, this.state.imageNaturalHeight * 2 || 4096),
			imageSmoothingEnabled: true,
			imageSmoothingQuality: "high",
		});

		if (!canvas) return;
		this.props.onChange?.(canvas.toDataURL("image/png"));
	};

	handleCanvasReady = (count = 0): void => {
		if (!this.cropper) return;

		const data = this.cropper.getCanvasData();
		const canvAspect = data.width / data.height;

		const cropBoxData = {
			width: this.aspectRatio > canvAspect ? (data.width * canvAspect) / this.aspectRatio : data.width,
			height: this.aspectRatio > canvAspect ? data.height : (data.height * canvAspect) / this.aspectRatio,
			top: data.top,
			left: data.left,
		};

		if (data.height < cropBoxData.height) {
			cropBoxData.top = data.top - (cropBoxData.height - data.height) / 2;
		} else if (data.width < cropBoxData.width) {
			cropBoxData.width = data.left - (cropBoxData.width - data.width) / 2;
		}

		if (cropBoxData.left <= 10 || cropBoxData.top <= 10) {
			this.cropper.zoom(-0.1);
			return this.handleCanvasReady(count + 1);
		}

		this.cropper.setCropBoxData(cropBoxData);
	};

	computeDimensions = () => {
		this.desiredWidth = this.props.desiredWidth;
		this.desiredHeight = this.props.desiredHeight;
		this.aspectRatio = this.desiredWidth / this.desiredHeight;
	};

	handleCropperRef = (cropper: Cropper): void => {
		this.cropper = cropper;
	};

	onImageError: React.ReactEventHandler<HTMLImageElement> = event => {
		this.setState({ urlDidError: event.currentTarget.src });
	};

	render(): React.ReactNode {
		return (
			<div
				className={cx("image-picker", styles.imagePicker)}
				style={{ width: this.props.previewWidth, height: this.props.previewHeight }}
			>
				<Dropzone
					ref={this.dropzone}
					multiple={false}
					accept="image/*"
					maxSize={4000000}
					onDropAccepted={this.accept}
					onDropRejected={this.reject}
				>
					{({ getRootProps, getInputProps }) => (
						<div
							{...getRootProps()}
							style={{ position: "absolute" }}
							className={cx(styles.dropzone, styles.transparencyIndicator, {
								[styles.circularDropzone]: this.props.circular,
							})}
						>
							<input {...getInputProps()} />
							<span className={styles.dropIcon} onClick={this.onUploadClicked}>
								<FontAwesomeIcon className="icon" icon={faImage} />
							</span>
							<img
								key={this.props.src}
								src={this.state.urlDidError === this.props.src ? this.props.placeholder : this.props.src}
								onError={this.onImageError}
							/>
						</div>
					)}
				</Dropzone>
			</div>
		);
	}
}
